import React, { useEffect, useState } from 'react';
import { MenuItem, Stack, TextField } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../../app/hooks';
import { 
  useUpsertRuleMutation, 
  useDeleteRuleMutation, 
  useGetMedicareContractsQuery,
  useGetFeeSchedulesByContractIdQuery,
  contractApi 
} from '../../../../../services/ContractService';
import { RuleCommands } from './RuleCommands';

interface PercentOfFeeScheduleProps {
  ruleId: string;
  ruleString?: string;
  ruleDescription?: string;
}

interface PercentOfFeeScheduleRule {
  linkedContract: string;
  feeSchedule: string;
  rate: string;
}

const PercentOfFeeSchedule: React.FC<PercentOfFeeScheduleProps> = ({ 
  ruleId, 
  ruleString = '', 
  ruleDescription = '' 
}) => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const company = searchParams.get('company');
  const dispatch = useAppDispatch();
  const [rule, setRule] = useState<PercentOfFeeScheduleRule>({ 
    linkedContract: '', 
    feeSchedule: '', 
    rate: '0' 
  });
  
  const { data: medicareContracts = [] } = useGetMedicareContractsQuery({ company: company || 'default' });
  const { data: feeSchedules = [] } = useGetFeeSchedulesByContractIdQuery(
    { contractId: rule.linkedContract || '0' },
    { skip: rule.linkedContract === '' }
  );
  
  const [upsertRule] = useUpsertRuleMutation();
  const [deleteRule] = useDeleteRuleMutation();

  const save = async () => {
    if (!contractId) return;
    
    try {
      await upsertRule({
        contractId: contractId,
        ruleId,
        ruleType: 'PercentOfFeeSchedule',
        priority: 0,
        ruleString: JSON.stringify(rule),
        description: ruleDescription || ''
      }).unwrap();
      
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const ruleDelete = async () => {
    try {
      await deleteRule({ ruleId }).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  useEffect(() => {
    if (ruleString && ruleString !== '') {
      try {
        const parsedRule: PercentOfFeeScheduleRule = JSON.parse(ruleString);
        setRule(parsedRule);
      } catch (error) {
        console.error('Failed to parse rule string:', error);
      }
    }
  }, [ruleString, ruleId]);

  return (
    <>
      <Stack direction="row" gap={1}>
        <TextField
          label="Contract"
          select
          size="small"
          sx={{ width: '250px' }}
          InputLabelProps={{ shrink: true }}
          value={rule.linkedContract}
          onChange={(e) => setRule({ ...rule, linkedContract: e.target.value })}
        >
          <MenuItem value="">Unknown</MenuItem>
          {medicareContracts.map((contract) => (
            <MenuItem key={contract.id} value={contract.id}>
              {contract.payer}
            </MenuItem>
          ))}
        </TextField>
        
        <TextField
          label="Fee Schedule"
          select
          size="small"
          sx={{ width: '450px' }}
          InputLabelProps={{ shrink: true }}
          value={rule.feeSchedule}
          onChange={(e) => setRule({ ...rule, feeSchedule: e.target.value })}
        >
          <MenuItem value="">Unknown</MenuItem>
          {feeSchedules.map((feeSchedule) => (
            <MenuItem key={feeSchedule.id} value={feeSchedule.id}>
              {feeSchedule.description}
            </MenuItem>
          ))}
        </TextField>
        
        <TextField
          label="Rate"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={rule.rate}
          onChange={(e) => setRule({ ...rule, rate: e.target.value })}
        />
      </Stack>
      
      <RuleCommands
        saveCommand={save}
        deleteCommand={ruleDelete}
        isNewRule={ruleId === '0'}
      />
    </>
  );
};

export { PercentOfFeeSchedule };